import { useLocation, matchPath } from 'react-router-dom';
import { AppNav } from '@m/alchemy-ui/AppNav';
import { AddIconOutlined } from '@m/alchemy-ui/icons/AddIconOutlined';
import { ThemeProvider, theme } from '@m/alchemy-ui/theme';
import { useSelectorTyped } from 'common/store/useSelectorTyped';
import {
  leaderboard,
  oneOnOnes,
  teamMemberOneOnOnes,
  performanceLink,
  performanceEmployeeLink,
  performanceTeamLink,
  qaLinks,
  streamLinks,
  rewardsReportsLinks,
  trendsLinks,
  qaPermissionNames
} from 'common/constants/menuItems';

import { toValidatedList } from 'common/utils/rbacHelpers';
import { useIsTablet } from 'common/hooks/breakpointHooks/useIsTablet';
import { RBAC_PRODUCT_AREAS } from 'common/constants/rbacProductAreas';
import { useDataAccessPolicies } from 'common/hooks/useDataAccessPolicies';
import { NavBarItem } from './types';
import MenuItem from './MenuItem';
import Trends from './Trends';
import RewardsReports from './RewardsReports';
import QA from './QA';
import { MobileMenu } from './MobileNav/MobileMenu';
import { useGetMyQaAssignmentsStatusQuery } from 'graphql.types';
import { useFeatureFlag } from 'common/hooks/useFeatureFlags';
import { NewQaReviewButton } from './Nav.styled';

export const Nav = () => {
  const permissions = useSelectorTyped(state => state.permissions);
  const currentPerson = useSelectorTyped(state => state.currentPerson);
  const indicatorEnabled = useFeatureFlag('assignments_indicator');

  const { currentDataAccessPolicies, hasRBACEnabled } = useDataAccessPolicies({
    productArea: RBAC_PRODUCT_AREAS.COACHING,
  });

  const isQaEnabled = permissions.qaEnabled;

  const { data } = useGetMyQaAssignmentsStatusQuery({
    fetchPolicy: 'cache-and-network',
    skip: !indicatorEnabled,
  });
  const hasChangedAssignments = data?.myQaAssignmentsStatus?.isChanged ?? false;
  const isMobileMenu = useIsTablet();

  const getQAMenuOrLinkItem = () => {
    let hasUserWithOneActivePerm = 0;

    qaPermissionNames.forEach(permObj => {
      const isActive = permissions[permObj.permissionName];
      if (isActive) {
        hasUserWithOneActivePerm += 1;
      }
    });

    if (hasUserWithOneActivePerm === 0) return {};

    if (hasUserWithOneActivePerm === 1) {
      const singleActivePermIdx = qaPermissionNames.findIndex(
        permObj => permissions[permObj.permissionName] === true
      );
      return {
        label: 'QA',
        url: qaPermissionNames[singleActivePermIdx]?.url ?? '',
        key: qaPermissionNames[singleActivePermIdx]?.key ?? '',
        icon: null,
      };
    } else {
      return qaLinks.qaMenu;
    }
  };

  const getRewardsReportsMenuOrLinkItem = () => {
    if (!(permissions.viewRewardsReports && permissions.rewardsEnabled))
      return [];
    if (permissions.thresholdsEnabled) {
      return [rewardsReportsLinks.rewardsReportsMenu];
    } else {
      return [rewardsReportsLinks.rewardsVotes];
    }
  };

  const { pathname } = useLocation();

  let allMenuItems: NavBarItem[] = [];

  const streamLink = currentPerson?.isAdmin
    ? streamLinks.streamStellaAdmin
    : streamLinks.streamStellaNonAdmin;

  const getPerformanceLink = () => {
    if (hasRBACEnabled) {
      switch (
        currentDataAccessPolicies[RBAC_PRODUCT_AREAS.COACHING]?.scope_name
      ) {
        case 'self': {
          return performanceEmployeeLink(currentPerson.id);
        }
        case 'own_team': {
          return performanceTeamLink(currentPerson.group_id);
        }
        default: {
          return performanceLink;
        }
      }
    } else {
      if (permissions.scorecardCompanyView) return performanceLink;
      if (permissions.scorecardTeamView)
        return performanceTeamLink(currentPerson.group_id);
      return performanceEmployeeLink(currentPerson.id);
    }
  };

  if (hasRBACEnabled) {
    // Only QA Product is enabled
    if (isQaEnabled && !permissions.feedbackEnabled) {
      const qaNavLinks: NavBarItem[] = [
        ...toValidatedList(
          permissions.viewQADashboard,
          qaLinks.dashboardOverview
        ),
        ...toValidatedList(permissions.reviewQA, qaLinks.interactions),
        ...toValidatedList(
          permissions.reviewQA || permissions.manageCalibrations,
          qaLinks.calibrations
        ),
        ...toValidatedList(permissions.viewAssignments, {
          ...qaLinks.assignments,
          showIndicator: hasChangedAssignments,
        }),
        ...toValidatedList(permissions.viewReviewers, qaLinks.reviewers),
        ...toValidatedList(permissions.reviewQA, qaLinks.newReview),
      ];

      allMenuItems = allMenuItems.concat(qaNavLinks);
    } else {
      const feedbackItems: NavBarItem[] = [
        ...toValidatedList(permissions.viewStream, streamLink),
        ...toValidatedList(permissions.feedbackEnabled, trendsLinks.trendsMenu),
        ...toValidatedList(isQaEnabled, getQAMenuOrLinkItem()),
      ];
      allMenuItems = [...allMenuItems, ...feedbackItems].filter(
        value => JSON.stringify(value) !== '{}'
      );
    }

    allMenuItems = [
      ...allMenuItems,
      ...(permissions.viewLeaderboard ? [leaderboard] : []),
      ...(permissions.oneOnOneLinks
        ? [permissions.manageOneOnOnes ? oneOnOnes : teamMemberOneOnOnes]
        : []),
      ...(permissions.viewPerformance ? [getPerformanceLink()] : []),
      ...getRewardsReportsMenuOrLinkItem(),
    ];
  } else {
    allMenuItems = [
      ...(permissions.feedbackEnabled ? [streamLink] : []),
      ...(permissions.feedbackEnabled ? [trendsLinks.trendsMenu] : []),

      ...(isQaEnabled
        ? permissions.manageQa
          ? [qaLinks.qaMenu]
          : [{ ...qaLinks.dashboardReviews, label: 'QA' }]
        : []),

      ...getRewardsReportsMenuOrLinkItem(),
      ...(permissions.oneOnOneLinks
        ? [permissions.manageOneOnOnes ? oneOnOnes : teamMemberOneOnOnes]
        : []),
      ...(permissions.performanceTabAvailable ? [getPerformanceLink()] : []),
      ...(permissions.leaderboardLink ? [leaderboard] : []),
    ];
  }

  const getMenus = () =>
    allMenuItems.map(({ getUrl, url, type, key, label, icon, showIndicator }) => {
      const path = getUrl ? getUrl(currentPerson.id) : url;

      if (type === 'nested' && key === 'trends') {
        return (
          <Trends
            key={key}
            permissions={permissions}
            pathname={pathname}
            hasRBACEnabled={hasRBACEnabled}
            isMobileMenu={isMobileMenu}
          />
        );
      }
      if (type === 'nested' && key === 'qa') {
        return (
          <QA
            key={key}
            hasChangedAssignments={hasChangedAssignments}
            isManageQA={permissions.manageQa}
            pathname={pathname}
            isMobileMenu={isMobileMenu}
          />
        );
      }
      if (type === 'nested' && key === 'rewards-reports') {
        return (
          <RewardsReports
            key={key}
            pathname={pathname}
            isMobileMenu={isMobileMenu}
          />
        );
      }
      if (key === 'qa-new-review') {
        return (
          <NewQaReviewButton
            key={key}
            href={url}
            icon={<AddIconOutlined aria-hidden="true" />}
          >
            {label}
          </NewQaReviewButton>
        );
      }

      const isLinkActive = Boolean(
        matchPath(pathname, {
          path: url,
          exact: key === 'view-stream',
        })
      );

      return (
        <MenuItem
          key={key}
          label={label}
          url={path}
          keyName={key}
          icon={icon}
          isActive={isLinkActive}
          isMobileMenu={isMobileMenu}
          showIndicator={showIndicator}
        />
      );
    });

  const MenuComponent = isMobileMenu ? MobileMenu : AppNav;

  return (
    <ThemeProvider theme={theme} colorScheme="alchemyDark">
      <MenuComponent>{getMenus()}</MenuComponent>
    </ThemeProvider>
  );
};
